import React, { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../api';
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  Divider,
  Paper,
  Grid,
  Button,
  Card,
  CardContent,
  CardHeader,
  Snackbar,
  Alert,
  Chip,
  Stack,
  CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

export default function RewardManager({ userId }) {
  const queryClient = useQueryClient();
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [amount, setAmount] = useState(1);
  const [description, setDescription] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get user data
  const { data: user } = useQuery({
    queryKey: ['user', userId],
    queryFn: async () => {
      if (!userId) return null;
      const response = await api.get(`/users/${userId}`);
      return response.data;
    },
    enabled: !!userId,
    staleTime: 60000 
  });

  // Fetch all assets for autocomplete (items and blueprints)
  const { data: allAssets = [], isLoading: assetsLoading } = useQuery({
    queryKey: ['allAssets'],
    queryFn: async () => {
      const response = await api.get('/inventories/items');
      return response.data;
    },
    staleTime: 5 * 60 * 1000
  });

  // Filter assets to only show items and blueprints (not NFTs)
  const availableAssets = useMemo(() => {
    return allAssets.filter(asset => {
      if (!asset.config) return false;
      try {
        const config = typeof asset.config === 'string' ? JSON.parse(asset.config) : asset.config;
        // Exclude NFTs, only include regular items and blueprints
        return !config.isNft && (asset.inventoryType === 'default' || asset.inventoryType === 'blueprints');
      } catch {
        return false;
      }
    });
  }, [allAssets]);

  // Filter assets based on search input
  const filteredAssets = useMemo(() => {
    if (!inputValue) return availableAssets.slice(0, 50); // Limit initial results
    
    const searchLower = inputValue.toLowerCase();
    return availableAssets.filter(asset => {
      const title = (asset.title || '').toLowerCase();
      const id = (asset.id || '').toLowerCase();
      return title.includes(searchLower) || id.includes(searchLower);
    }).slice(0, 50);
  }, [availableAssets, inputValue]);

  const handleCreateReward = async () => {
    if (!selectedAsset || amount < 1 || !description.trim()) {
      setSnackbar({ 
        open: true, 
        message: 'Please select an item, set amount (min 1), and provide a description', 
        severity: 'error' 
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await api.post(`/users/${userId}/rewards`, {
        rewardId: selectedAsset.id,
        rewardAmount: amount,
        description: description.trim(),
        rewardType: 'AdminReward'
      });
      
      setSnackbar({ 
        open: true, 
        message: `Reward created successfully! ${amount}x ${selectedAsset.title} will appear in user's inbox.`, 
        severity: 'success' 
      });
      
      // Reset form
      setSelectedAsset(null);
      setInputValue('');
      setAmount(1);
      setDescription('');
      
      // Invalidate rewards query to refresh if user views rewards
      queryClient.invalidateQueries(['userRewards', userId]);
    } catch (error) {
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.error || 'Failed to create reward', 
        severity: 'error' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (!userId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Please select a user to manage rewards</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Create Inbox Reward
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Add rewards that will appear in the user&apos;s inbox. Users can claim these rewards from the Unity client.
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Autocomplete
              options={filteredAssets}
              getOptionLabel={(option) => `${option.title || 'Unknown'} (${option.id})`}
              value={selectedAsset}
              onChange={(event, newValue) => {
                setSelectedAsset(newValue);
                if (newValue && !description) {
                  setDescription(`Admin Reward: ${newValue.title || 'Item'}`);
                }
              }}
              inputValue={inputValue}
              onInputChange={(event, newInputValue) => {
                setInputValue(newInputValue);
              }}
              loading={assetsLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search Item or Blueprint"
                  placeholder="Type to search by name or GUID..."
                  helperText="Search for items or blueprints to add as rewards"
                />
              )}
              renderOption={(props, option) => {
                // Parse config to extract item details
                let itemType = 'Item';
                let stackSize = null;
                let rarity = null;
                let inventoryType = option.inventoryType || 'default';
                
                if (option.config) {
                  try {
                    const configObj = typeof option.config === 'string' ? JSON.parse(option.config) : option.config;
                    if (configObj) {
                      if (configObj.type) itemType = configObj.type;
                      if (configObj.stackSize !== undefined && configObj.stackSize !== null) {
                        stackSize = configObj.stackSize;
                      }
                      if (configObj.rarity) rarity = configObj.rarity;
                      if (configObj.inventoryType) inventoryType = configObj.inventoryType;
                    }
                  } catch (e) {
                    // Config parse failed, use defaults
                  }
                }
                
                return (
                  <Box component="li" {...props} key={option.id} sx={{ py: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center" width="100%">
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {option.title || 'Unknown Item'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                          {option.id}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" justifyContent="flex-end">
                        {stackSize !== null && stackSize !== undefined && stackSize > 0 && (
                          <Chip 
                            label={`Stack: ${stackSize}`} 
                            size="small" 
                            sx={{ 
                              height: '20px',
                              fontSize: '0.7rem',
                              bgcolor: 'grey.800', 
                              color: 'grey.200',
                            }} 
                          />
                        )}
                        <Chip 
                          label={itemType} 
                          size="small" 
                          color="primary"
                          sx={{ 
                            height: '20px',
                            fontSize: '0.7rem',
                          }} 
                        />
                        <Chip 
                          label={inventoryType} 
                          size="small" 
                          color="secondary"
                          variant="outlined"
                          sx={{ 
                            height: '20px',
                            fontSize: '0.7rem',
                          }} 
                        />
                        {rarity && (
                          <Chip 
                            label={rarity} 
                            size="small" 
                            sx={{ 
                              height: '20px',
                              fontSize: '0.7rem',
                              bgcolor: rarity === 'Legendary' ? 'warning.main' : 
                                      rarity === 'Epic' ? 'purple' : 
                                      rarity === 'Rare' ? 'info.main' : 'grey.600',
                              color: 'white',
                            }} 
                          />
                        )}
                      </Stack>
                    </Stack>
                  </Box>
                );
              }}
              noOptionsText={inputValue ? "No items found" : "Start typing to search..."}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Amount"
              type="number"
              value={amount}
              onChange={(e) => {
                const val = parseInt(e.target.value) || 1;
                setAmount(Math.max(1, val));
              }}
              inputProps={{ min: 1, max: 100000 }}
              helperText="Number of items to reward"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g., Admin Reward: Stone Shovel"
              helperText="This description will be shown to the user in their inbox"
              multiline
              rows={2}
            />
          </Grid>

          {selectedAsset && (
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>
                    Selected Reward Preview
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Chip label={`${amount}x`} color="primary" />
                    <Chip label={selectedAsset.title || 'Unknown'} />
                    <Chip label={selectedAsset.inventoryType || 'default'} size="small" variant="outlined" />
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          )}

          <Grid item xs={12}>
            <Button
              variant="contained"
              color="primary"
              startIcon={isSubmitting ? <CircularProgress size={20} /> : <AddIcon />}
              onClick={handleCreateReward}
              disabled={!selectedAsset || amount < 1 || !description.trim() || isSubmitting}
              fullWidth
              size="large"
            >
              {isSubmitting ? 'Creating Reward...' : 'Create Inbox Reward'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography variant="body2">
          <strong>Note:</strong> Rewards created here will appear in the user&apos;s inbox in the Unity client. 
          Users must claim them manually. The reward will be added to their inventory when claimed.
        </Typography>
      </Alert>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

